import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CaseCard = ({ caseData }) => {
  const navigate = useNavigate();

  const statusConfig = {
    pending: {
      color: 'text-warning',
      bgColor: 'bg-warning/10',
      icon: 'Clock',
      label: 'Pending Analysis',
    },
    'in-progress': {
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      icon: 'Loader',
      label: 'In Progress',
    },
    'clarification-needed': {
      color: 'text-warning',
      bgColor: 'bg-warning/10',
      icon: 'AlertCircle',
      label: 'Clarification Needed',
    },
    completed: {
      color: 'text-success',
      bgColor: 'bg-success/10',
      icon: 'CheckCircle',
      label: 'Completed',
    },
  };

  const config = statusConfig?.[caseData?.status] || statusConfig?.pending;

  const handleViewClick = () => {
    if (caseData?.status === 'clarification-needed') {
      navigate(`/clarifying-questions/${caseData?.caseId}`);
    } else if (caseData?.status === 'completed') {
      navigate(`/report-viewer/${caseData?.caseId}`);
    } else {
      navigate(`/report-viewer/${caseData?.caseId}`);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 flex flex-col gap-3 transition-smooth hover:shadow-elevation-md">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          <Icon name="FileText" size={16} className="text-primary mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <h3 className="font-semibold text-sm text-foreground leading-snug truncate">
              {caseData?.caseName}
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">#{caseData?.caseNumber}</p>
          </div>
        </div>
        <div className={`flex-shrink-0 flex items-center gap-1 px-2 py-1 rounded-md ${config?.bgColor}`}>
          <Icon name={config?.icon} size={12} className={config?.color} />
          <span className={`text-xs font-medium ${config?.color} whitespace-nowrap`}>{config?.label}</span>
        </div>
      </div>

      {/* Meta */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Icon name="Calendar" size={12} />
          <span>{caseData?.submissionDate}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Icon name="User" size={12} />
          <span>{caseData?.persona}</span>
        </div>
        {caseData?.expertAssigned && (
          <div className="flex items-center gap-1.5 text-xs text-success">
            <Icon name="UserCheck" size={12} />
            <span>{caseData?.expertAssigned}</span>
          </div>
        )}
      </div>

      {/* Progress bar */}
      {caseData?.progress && caseData?.status === 'in-progress' && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-muted-foreground">Progress</span>
            <span className="text-xs font-medium text-foreground">{caseData?.progress}%</span>
          </div>
          <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-500"
              style={{ width: `${caseData?.progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 mt-auto pt-1">
        <Button
          variant="default"
          size="sm"
          onClick={handleViewClick}
          iconName={caseData?.status === 'clarification-needed' ? 'MessageSquare' : caseData?.status === 'completed' ? 'Eye' : 'Activity'}
          iconPosition="left"
          className="flex-1"
        >
          {caseData?.status === 'clarification-needed' ? 'Clarify' : caseData?.status === 'completed' ? 'View Report' : 'View Status'}
        </Button>
        {(caseData?.status === 'completed' || caseData?.status === 'in-progress') && !caseData?.expertAssigned && (
          <Button
            variant="outline"
            size="sm"
            iconName="Users"
            iconPosition="left"
            onClick={() => navigate('/expert-marketplace-listing')}
          >
            Hire
          </Button>
        )}
      </div>
    </div>
  );
};

export default CaseCard;